// src/components/common/UIPlayground.jsx
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";

/**
 * Full UIPlayground
 * Shows one pane per component in src/components/common/
 * - Interactive demos for each component
 * - Uses direct imports from common; if a component is missing it falls back to a simple placeholder
 *
 * Paste this at: src/components/common/UIPlayground.jsx
 */

import Button from "./Button";
import TextInput from "./TextInput";
import TextArea from "./TextArea";
import SelectDropdown from "./SelectDropdown";
import DatePicker from "./DatePicker";
import Card from "./Card";
import StatCard from "./StatCard";
import ProgressBar from "./ProgressBar";
import Badge from "./Badge";
import Modal from "./Modal";
import Loading from "./Loading";
import ShimmerLoader from "./ShimmerLoader";
import GPXUpload from "./GPXUpload";
import Table from "./Table";
import Pagination from "./Pagination";
import SearchBar from "./SearchBar";
import ConfirmDialog from "./ConfirmDialog";
import Tabs from "./Tabs";
import MultiSelect from "./MultiSelect";
import DualRangeSlider from "./DualRangeSlider";
import FilterPanel from "./FilterPanel";

function Pane({ title, children, note }) {
  return (
    <div
      className="card dark:bg-gray-800 dark:border-gray-700"
      style={{ padding: 16, marginBottom: 16 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        <div>
          <h4 className="dark:text-white" style={{ margin: 0 }}>
            {title}
          </h4>
          {note && (
            <div
              className="text-gray-600 dark:text-gray-400"
              style={{ marginTop: 6, fontSize: 13 }}
            >
              {note}
            </div>
          )}
        </div>
      </div>
      <div style={{ marginTop: 12 }}>{children}</div>
    </div>
  );
}

export default function UIPlayground() {
  // inputs demo state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [date, setDate] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [progress, setProgress] = useState(38);
  const [statValue, setStatValue] = useState(124);
  const [stepIndex, setStepIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("tab1");
  const [selectedStates, setSelectedStates] = useState([]);
  const [distanceRange, setDistanceRange] = useState([20, 80]);
  const [demoFilterValue, setDemoFilterValue] = useState("");

  const fileRef = useRef(null);

  const dropdownOptions = [
    { value: "run", label: "Trail run" },
    { value: "ride", label: "Road ride" },
    { value: "hike", label: "Hike" },
    { value: "walk", label: "Walk" },
    { value: "fly", label: "FLy" },
    { value: "talk", label: "Talk" },
    { value: "cook", label: "Cook" },
    { value: "pray", label: "Pray" },
  ];

  useEffect(() => {
    // small sample to show ShimmerLoader & Loading states if they have internal behaviour
    // nothing required here, but keep effect to indicate playground lifecycle
    return () => {};
  }, []);

  return (
    <div
      className="bg-white dark:bg-gray-900 min-h-screen"
      style={{ padding: 24, maxWidth: 1100, margin: "0 auto 60px" }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <div>
          <h2 className="dark:text-white" style={{ margin: 0 }}>
            UI Playground
          </h2>
          <div
            className="text-gray-600 dark:text-gray-400"
            style={{ fontSize: 13 }}
          >
            Interactive panes for each component — test props and behavior here.
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link
            to="/"
            className="text-blue-600 dark:text-blue-400 hover:underline"
            style={{ fontSize: 13 }}
          >
            ← Back
          </Link>
        </div>
      </header>

      {/* Buttons */}
      <Pane title="Buttons" note="Variants, sizes, loading & disabled states">
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button size="sm">Small</Button>
          <Button size="lg">Large</Button>
          <Button loading>Loading</Button>
          <Button disabled>Disabled</Button>
        </div>
      </Pane>

      {/* Inputs */}
      <Pane title="Text Input" note="Label, icon slot, error state">
        <div style={{ maxWidth: 560 }}>
          <TextInput
            label="Full name"
            id="demo-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
          />

          <TextInput
            label="Email (with error)"
            id="demo-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@site.com"
            error={
              email && !email.includes("@")
                ? "Must be a valid email"
                : undefined
            }
          />
        </div>
      </Pane>

      {/* TextArea */}
      <Pane title="TextArea" note="Clear, char counter, maxLength">
        <TextArea
          label="Bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          placeholder="A short bio..."
          maxLength={300}
        />
      </Pane>

      {/* Select / Dropdown */}
      <Pane title="Select / Dropdown" note="Custom select wrapper.">
        <div style={{ maxWidth: 420 }}>
          <SelectDropdown
            label="Activity type"
            value={selectedOption}
            onChange={(e) => setSelectedOption(e.target.value)}
            options={dropdownOptions}
            placeholder="Choose activity..."
          />
          <div
            className="text-gray-700 dark:text-gray-300"
            style={{ marginTop: 8 }}
          >
            Selected: <strong>{selectedOption || "(none)"}</strong>
          </div>
        </div>
      </Pane>

      {/* DatePicker */}
      <Pane title="DatePicker" note="Native date input wrapper">
        <DatePicker
          label="Date of activity"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          showAge={true}
        />
        <div
          className="text-gray-700 dark:text-gray-300"
          style={{ marginTop: 8 }}
        >
          Selected date: <strong>{date || "(none)"}</strong>
        </div>
      </Pane>

      {/* GPX Upload */}
      <Pane title="GPX Upload">
        <GPXUpload onUpload={(f) => console.log("gpx uploaded", f)} />
      </Pane>

      {/* Badges */}
      <Pane title="Badges" note="Role badges + failure badge with animations">
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Badge tone="admin" size="md">
            Admin
          </Badge>
          <Badge tone="organiser" size="md">
            Organiser
          </Badge>
          <Badge tone="sponsor" size="md">
            Sponsor
          </Badge>
          <Badge tone="rider" size="md">
            Rider
          </Badge>

          <Badge tone="danger" size="md">
            Failed
          </Badge>

          {/* small / icon variants */}
          <Badge
            tone="success"
            size="sm"
            icon={
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            Verified
          </Badge>

          <Badge tone="info" size="lg" animated="shine">
            Beta
          </Badge>
        </div>
      </Pane>

      {/* Progress bar */}
      <Pane title="ProgressBar" note="Adjustable percent">
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            maxWidth: 420,
          }}
        >
          <div style={{ flex: 1 }}>
            <ProgressBar percent={progress} />
          </div>
          <div style={{ width: 160 }}>
            <input
              className="input-field"
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={(e) => setProgress(Number(e.target.value))}
            />
            <div
              className="text-gray-700 dark:text-gray-300"
              style={{ fontSize: 13, marginTop: 6 }}
            >
              {progress}%
            </div>
          </div>
        </div>
      </Pane>

      {/* Cards */}
      <Pane title="Cards / StatCard">
        <div style={{ display: "grid", gap: 12 }}>
          <Card title="Basic Card">
            <div>
              This is a simple card body. You can pass header/footer props.
            </div>
          </Card>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ width: 280 }}>
              <StatCard
                title="Total Rides"
                value={`${statValue}`}
                delta="+4% this week"
              />
            </div>
          </div>
        </div>
      </Pane>

      {/* Modal demo */}
      <Pane title="Modal (accessible)" note="Open/close modal demo">
        <div style={{ display: "flex", gap: 12 }}>
          <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
        </div>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Demo Modal"
        >
          <p>This is a demo modal. Close to return.</p>
        </Modal>
      </Pane>

      {/* Loading & Shimmer */}
      <Pane title="Loading & Shimmer (separate)">
        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          <div>
            <div
              className="text-gray-700 dark:text-gray-300"
              style={{ fontSize: 13, marginBottom: 8 }}
            >
              Loading component
            </div>
            <Loading size={36} />
          </div>
          <div>
            <div
              className="text-gray-700 dark:text-gray-300"
              style={{ fontSize: 13, marginBottom: 8 }}
            >
              Shimmer placeholder
            </div>
            <ShimmerLoader />
          </div>
        </div>
      </Pane>

      {/* Table Demo */}
      <Pane title="Table" note="Reusable table component">
        <Table
          columns={[
            { key: "id", title: "ID" },
            { key: "name", title: "Name" },
            { key: "role", title: "Role" },
          ]}
          data={[
            { id: 1, name: "Alice", role: "Admin" },
            { id: 2, name: "Bob", role: "User" },
          ]}
          renderRow={(row) => (
            <tr
              key={row.id}
              className="hover:bg-gray-50 dark:hover:bg-gray-900"
            >
              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                {row.id}
              </td>
              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                {row.name}
              </td>
              <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                {row.role}
              </td>
            </tr>
          )}
        />
      </Pane>

      {/* SearchBar & Pagination */}
      <Pane title="Data Tools" note="Search and Pagination">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <SearchBar placeholder="Search items..." />
          <Pagination
            page={1}
            totalPages={5}
            onChange={(p) => console.log("page", p)}
          />
        </div>
      </Pane>

      {/* ConfirmDialog Demo */}
      <Pane title="Confirm Dialog">
        <Button onClick={() => setConfirmOpen(true)}>
          Open Confirm Dialog
        </Button>
        <ConfirmDialog
          open={confirmOpen}
          title="Confirm Action"
          message="Are you sure you want to proceed?"
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            alert("Confirmed");
            setConfirmOpen(false);
          }}
        />
      </Pane>

      {/* Tabs Demo */}
      <Pane title="Tabs" note="Tab navigation component">
        <Tabs
          tabs={[
            { id: "tab1", label: "Dashboard", count: 12 },
            { id: "tab2", label: "Settings" },
            { id: "tab3", label: "Analytics", count: 5 },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <div
          className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          style={{
            marginTop: 16,
            padding: 12,
            borderRadius: 8,
          }}
        >
          Active Tab: <strong>{activeTab}</strong>
        </div>
      </Pane>

      {/* MultiSelect Demo */}
      <Pane title="MultiSelect" note="Searchable multi-select dropdown">
        <MultiSelect
          label="Select States"
          options={[
            { value: "PB", label: "Punjab" },
            { value: "KA", label: "Karnataka" },
            { value: "MH", label: "Maharashtra" },
            { value: "DL", label: "Delhi" },
            { value: "GA", label: "Goa" },
          ]}
          selected={selectedStates}
          onChange={setSelectedStates}
          placeholder="Search states..."
        />
      </Pane>

      {/* DualRangeSlider Demo */}
      <Pane title="Dual Range Slider" note="Two-thumb range slider">
        <DualRangeSlider
          label="Distance Range"
          min={0}
          max={100}
          value={distanceRange}
          onChange={setDistanceRange}
          unit="km"
          step={5}
        />
      </Pane>

      {/* FilterPanel Demo */}
      <Pane title="Filter Panel" note="Collapsible filter container">
        <FilterPanel
          hasActiveFilters={demoFilterValue !== ""}
          onClearFilters={() => setDemoFilterValue("")}
          title="Demo Filters"
          defaultOpen={true}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <label>
              <div
                className="text-gray-700 dark:text-gray-300"
                style={{ fontSize: 13, marginBottom: 6 }}
              >
                Search
              </div>
              <input
                type="text"
                value={demoFilterValue}
                onChange={(e) => setDemoFilterValue(e.target.value)}
                placeholder="Type to see clear button..."
                className="input-field"
                style={{ width: "100%" }}
              />
            </label>
            <div
              className="text-gray-600 dark:text-gray-400"
              style={{ fontSize: 13 }}
            >
              Try typing something to activate the clear button.
            </div>
          </div>
        </FilterPanel>
      </Pane>
    </div>
  );
}
